// ======================= IMPORTS =======================
import { useState, useEffect } from "react";
import { secureStorage } from "@/utils/secureStorage";

import {
  Home,
  Waves,
  ListChecks,
  Wrench,
  Bot,
  Clock,
  Share2,
  PlusSquare,
  MapPinPlus
} from "lucide-react";

import {
  Newspaper,
  MessageSquare,
  Users,
  Search,
  ChevronDown,
  ChevronRight
} from "lucide-react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "./ui/sidebar";

import { useSidebar } from "./ui/useSidebar";
import { Badge } from "./ui/badge";
import { useTheme } from "next-themes";


// ======================= TYPES =======================
interface AppSidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
}


// ======================= SHARE SUBMENU ITEMS =======================
const shareSubItems = [
  { key: "find", label: "Find", icon: Search },
  { key: "feed", label: "Feed", icon: Newspaper },
  { key: "messages", label: "Messages", icon: MessageSquare },
  { key: "team", label: "Team", icon: Users },
];


// ======================= MAIN NAVIGATION ITEMS =======================
const navigationItems = [
  {
    title: "Home",
    key: "home",
    icon: Home,
  },
  {
    title: "Practice",
    key: "practice",
    icon: Waves,
    badge: "New",
  },
  {
    title: "Create Set",
    key: "create-set",
    icon: ListChecks,
  },
  {
    title: "Tools",
    key: "tools",
    icon: Wrench,
  },
  {
    title: "AI Assistance",
    key: "ai-assistance",
    icon: Bot,
    badge: "Beta",
  },
  {
    title: "Timer",
    key: "practiceclock",
    icon: Clock,
  },
  {
    title: "Share",
    key: "share",
    icon: Share2,
  },
  {
    title: "My Blogs",
    key: "create-blog",
    icon: PlusSquare,
    superAdminOnly: true 
  },
  {
    title: "Manage Pools",
    key: "add-pool",
    icon: MapPinPlus,
    superAdminOnly: true 
  },
];


// ======================= SIDEBAR COMPONENT =======================
export function AppSidebar({ activeSection, onSectionChange }: AppSidebarProps) {
  const { toggleSidebar } = useSidebar();
  const { theme } = useTheme();
  const logoSrc = theme === "dark" ? "/logo_white.png" : "/logo.png";

  // Track whether Share submenu is open
  const [shareOpen, setShareOpen] = useState(false);

  // Read user type once
  const [userType, setUserType] = useState<string | null>(null);

  useEffect(() => {
    setUserType(secureStorage.get("user_type"));
  }, []);

  // Auto close submenu when leaving Share pages
  useEffect(() => {
    if (!activeSection.startsWith("share")) {
      setShareOpen(false);
    }
  }, [activeSection]);

  // Resolve active section so only one menu highlights
  const resolvedActive = (() => {
    if (activeSection === "practiceclock") return "practiceclock";
    if (activeSection.startsWith("share")) return "share";
    return activeSection;
  })();

  return (
    <Sidebar
      variant="sidebar"
      collapsible="offcanvas"
      className="glass-sidebar transition-all duration-300 fixed right-0 top-0 h-full z-40 w-full max-w-sm md:w-80 xl:left-0 xl:w-64"
    >
      
      <SidebarContent>
        <button
          onClick={toggleSidebar}
          className="flex items-center justify-center p-4 border-b w-full hover:bg-sidebar-accent transition-colors cursor-pointer"
        >
          <img src={logoSrc} alt="Swimnerd Logo" className="h-12 w-auto" />
        </button>

        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>

              {navigationItems
                .filter(item => {
                  if (item.superAdminOnly) {
                    return userType === "superadmin";
                  }
                  return true;
                })
                .map((item) => (
                  <div key={item.key}>

                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={resolvedActive === item.key}
                        onClick={() => {
                          if (item.key === "share") {
                            const newState = !shareOpen;
                            setShareOpen(newState);

                            if (newState) {
                              onSectionChange("share-find");
                            }

                            if (window.innerWidth < 768) toggleSidebar();
                            return;
                          }

                          onSectionChange(item.key);

                          if (window.innerWidth < 768) toggleSidebar();
                        }}
                        className="flex items-center justify-between cursor-pointer"
                      >
                        <div className="flex items-center">
                          <item.icon className="mr-2 h-4 w-4" />
                          <span>{item.title}</span>
                        </div>

                        {item.badge && (
                          <Badge variant="secondary" className="ml-auto">
                            {item.badge}
                          </Badge>
                        )}

                        {item.key === "share" && (
                          shareOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>

                    {/* submenu OUTSIDE ShadCN wrapper */}
                    {item.key === "share" && shareOpen && (
                      <div className="ml-8 mt-2 space-y-1">
                        {shareSubItems.map((sm) => {
                          const Icon = sm.icon;
                          return (
                            <button
                              key={sm.key}
                              onClick={() => {
                                onSectionChange(`share-${sm.key}`);
                                if (window.innerWidth < 768) toggleSidebar();
                              }}
                              className={`flex items-center gap-2 text-left w-full px-2 py-1 rounded text-sm transition ${
                                activeSection === `share-${sm.key}`
                                  ? "bg-sidebar-accent text-black"
                                  : "text-sidebar-foreground hover:bg-sidebar-accent/30"
                              }`}
                            >
                              <Icon className="h-4 w-4" />
                              {sm.label}
                            </button>
                          );
                        })}
                      </div>
                    )}

                  </div>
                ))}

            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

     {/* TODO : Version Should Be Dynamic Form API  */}

      {/* FOOTER */}
      <SidebarFooter>
        <div className="p-4 text-center text-sm text-muted-foreground">
          <p>Swimnerd v2.1</p>
          <p>Unleash Your Speed</p>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
